import Alert from "@mui/lab/Alert";
import {
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Grid2,
	Snackbar,
	SnackbarCloseReason,
	TextField,
	Typography,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../../components/modals/error-modal";
import { CenteredLazyLoading } from "../../../../../components/shared/CenteredLazyLoading";
import { AssetTreePicker } from "../../../../../controls/asset-tree-picker/AssetTreePicker";
import { HelmetDexteel } from "../../../../../helmet/HelmetDexteel";
import { ASSET_INITIAL_VALUES, Asset } from "../../../../../models/Asset";
import { useSearchAssets } from "../../hooks/useSearchAssets";
import { AssetParameters } from "../../models/AssetParameters";
import { insertAsset } from "../../repositories/AssetRepository";

type Props = {
	assetNodeSelectedInTree: AssetParameters | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
	suffixTitle?: string;
};

export const CreateNewAssetDialog = ({
	assetNodeSelectedInTree,
	show,
	onHide,
	suffixTitle,
}: Props) => {
	const [assetCreatedSuccess, setAssetCreatedSuccess] =
		useState<boolean>(false);
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);

	const {
		data: rows,
		isLoading,
		isError,
		error: e,
		isSuccess,
	} = useSearchAssets();

	const { control, handleSubmit, reset, setValue } = useForm<Asset>({
		defaultValues: ASSET_INITIAL_VALUES,
	});

	const handleClose = (
		event: React.SyntheticEvent<any> | Event,
		reason?: SnackbarCloseReason,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAssetCreatedSuccess(false);
	};

	const createAsset = useMutation({
		mutationFn: insertAsset,
		onSuccess: () => {
			setAssetCreatedSuccess(true);
			onHide(true);
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => {
			setIsSubmitLoading(false);
		},
	});

	const handleCreateAsset = async (data: Asset) => {
		setIsSubmitLoading(true);

		await createAsset.mutate({
			AssetName: data.AssetName,
			ParentAssetId: data.ParentAssetId!,
			CanBeDefaultAsset: data.CanBeDefaultAsset,
		});
	};

	const handleFormSubmit = handleSubmit(handleCreateAsset);

	useEffect(() => {
		if (show) {
			reset(ASSET_INITIAL_VALUES);
			if (assetNodeSelectedInTree?.id) {
				setValue("ParentAssetId", assetNodeSelectedInTree?.id);
			} else {
				const rootId = rows?.find((a) => a.ParentAssetId === null)?.AssetId;
				setValue("ParentAssetId", rootId ?? null);
			}
		}
	}, [show, assetNodeSelectedInTree, isSuccess]);

	useEffect(() => {
		if (e && isError) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			{show && (
				<HelmetDexteel
					title={`New Asset${suffixTitle ? ` - ${suffixTitle}` : ""}`}
				/>
			)}
			<Dialog
				fullWidth
				maxWidth="sm"
				open={show}
				onClose={() => onHide(false)}
				disableEnforceFocus
				sx={{
					"& .input-group-text": {
						display: "none",
					},
					"& .MuiPaper-root": {
						height: "auto",
					},
				}}
			>
				{isLoading ? (
					<div
						style={{ minHeight: 300, alignItems: "center", display: "flex" }}
					>
						<CenteredLazyLoading />
					</div>
				) : (
					<>
						<DialogTitle>
							<Grid2 size={{ md: 12, xs: 12 }}>
								<Typography variant="h5">NEW ASSET</Typography>
							</Grid2>
						</DialogTitle>
						<form onSubmit={handleFormSubmit}>
							<DialogContent dividers>
								<Grid2 container spacing={1}>
									<Grid2 size={{ md: 12, xs: 12 }}>
										<Controller
											name="AssetName"
											control={control}
											rules={{
												validate: {
													notEmpty: (value) => value.trim() !== "",
												},
											}}
											render={({
												field: { onChange, value },
												fieldState: { error },
											}) => (
												<>
													<TextField
														label="Asset Name"
														error={!!error}
														helperText={error?.message}
														autoComplete="off"
														onChange={onChange}
														value={value}
													/>
												</>
											)}
										/>
									</Grid2>
									<Grid2 container>
										<Grid2 size={{ md: 12, xs: 12 }}>
											<Controller
												name={"ParentAssetId"}
												control={control}
												render={({ field, fieldState: { error } }) => (
													<AssetTreePicker
														label="Parent"
														value={field.value}
														onChange={field.onChange}
														fieldError={error}
													/>
												)}
											/>
										</Grid2>
										<Grid2 size={{ xs: 6, md: 6 }}>
											<Controller
												name="CanBeDefaultAsset"
												control={control}
												defaultValue={false}
												render={({ field }) => (
													<FormControlLabel
														sx={{ userSelect: "none" }}
														control={
															<Checkbox
																checked={field.value as boolean}
																onChange={(e) =>
																	field.onChange(e.target.checked)
																}
																name="CanBeDefaultAsset"
																color="primary"
															/>
														}
														label="Can be Default Asset"
													/>
												)}
											/>
										</Grid2>
									</Grid2>
								</Grid2>
							</DialogContent>
							<DialogActions>
								<Grid2 container spacing={1} justifyContent="flex-end">
									<Grid2 size={{ md: 6, xs: 12 }}>
										<Button
											fullWidth
											variant="contained"
											color="inherit"
											onClick={() => onHide(false)}
										>
											Cancel
										</Button>
									</Grid2>
									<Grid2 size={{ md: 6, xs: 12 }}>
										<Button
											fullWidth
											startIcon={
												isSubmitLoading && <CircularProgress size="1rem" />
											}
											disabled={isSubmitLoading}
											variant="contained"
											color="primary"
											type="submit"
										>
											Save
										</Button>
									</Grid2>
								</Grid2>
							</DialogActions>
						</form>
					</>
				)}
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Snackbar
				open={assetCreatedSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Document created successfully
				</Alert>
			</Snackbar>
		</>
	);
};
